import React, { Component, ReactElement } from "react";

// css
import "./style.scss";

/**
 * Props interface for the Teletype component
 * @interface TeletypeProps
 * @property {string} text - The text content to animate
 * @property {string} [className] - Optional CSS class for styling
 * @property {boolean} [autostart=true] - Whether to start animation immediately
 * @property {boolean} [autocomplete=false] - Whether to skip animation and render fully
 * @property {number} [speed=5] - Animation speed in milliseconds
 * @property {() => void} onComplete - Callback function called when animation completes
 * @property {() => void} [onNewLine] - Optional callback function called when cursor moves to a new line
 */
interface TeletypeProps {
    text: string; // text to animate
    className?: string; // css class
    autostart?: boolean; // start animating immediately? default = true
    autocomplete?: boolean; // skip animating and instead fully render? default = false
    speed?: number; // optional animation speed in ms; default = 5

    onComplete: () => void; // event called on completion
    onNewLine?: () => void; // event called when the cursor is moved to a new line
}

/**
 * State interface for the Teletype component
 * @interface TeletypeState
 * @property {number} index - Current position in the text
 * @property {number} char - Current character being rendered
 * @property {boolean} active - Whether the component is currently animating
 * @property {boolean} done - Whether the animation has completed
 * @property {boolean} paused - Whether the animation is paused
 */
interface TeletypeState {
    index: number;
    char: number;
    active: boolean;
    done: boolean;
    paused: boolean;
}

/**
 * A component that animates text display character by character, creating a typewriter effect.
 * It supports customizable animation speed, auto-start/complete options, and provides
 * callbacks for animation completion and new line events.
 * 
 * @component
 * @example
 * ```tsx
 * <Teletype 
 *   text="Hello, I am typing..."
 *   speed={10}
 *   autostart={true}
 *   onComplete={() => console.log('Animation complete')}
 *   onNewLine={() => console.log('New line started')}
 * />
 * ```
 */
class Teletype extends Component<TeletypeProps, TeletypeState> {
    /** Interval between cursor movements in milliseconds */
    private _cursorInterval = 5;
    /** Timer ID for the animation setTimeout */
    private _animateTimerId: number = null;
    /** Reference to the cursor element for position tracking */
    private _cursorRef: React.RefObject<HTMLElement> = null;
    /** Current vertical position of the cursor */
    private _cursorY: number = null;

    constructor(props: TeletypeProps) {
        super(props);

        this._cursorRef = React.createRef<HTMLElement>();
        this._cursorY = 0;

        const done = !!props.autocomplete;
        const paused = props.autostart === false;

        this._cursorInterval = props.speed || this._cursorInterval;

        this.state = {
            index: 0,
            char: 0,
            active: false,
            done,
            paused,
        };

        this._animate = this._animate.bind(this);
        this._updateState = this._updateState.bind(this);
    }

    /**
     * Renders the component with visible text, cursor, and hidden text
     * @returns {ReactElement | null} The rendered component or null if inactive/done
     */
    public render(): ReactElement {
        const { text, className } = this.props;
        const { char, done, active, } = this.state;

        const visible = text.substr(0, char); // already rendered
        const cursor = text.substr(char, 1) || " "; // " " ensures the curosr is briefly visible for line breaks
        const hidden = text.substr(char + 1); // to be rendered

        if (!active || done) {
            return null;
        }

        const css = ["__teletype__", className ? className : null].join(" ").trim();

        return (
            <div className={css}>
                <span className="visible">{visible}</span>
                <span className="cursor" ref={this._cursorRef}>{cursor}</span>
                <span className="hidden">{hidden}</span>
            </div>
        );
    }

    /**
     * Sets up the component after mounting, starting animation if appropriate
     */
    public componentDidMount(): void {
        const { paused, done } = this.state;

        // if autocomplete is on, we can skip to the end
        if (done) {
            this._onComplete();
            return;
        }

        // ready to go
        if (!paused) {
            this.setState({
                active: true,
            }, () => this._animate());
        }
    }

    /**
     * Handles component updates, managing animation state and completion
     * @param prevProps - Previous component props
     * @param prevState - Previous component state
     */
    public componentDidUpdate(prevProps: TeletypeProps, prevState: TeletypeState): void {
        if (!prevState.done && this.state.done) {
            this._onComplete();
        }


        if (this.state.done) {
            return;
        }

        this._animate();
    }

    /**
     * Cleans up timers when component unmounts
     */
    public componentWillUnmount(): void {
        if (this._animateTimerId !== null) {
            clearTimeout(this._animateTimerId);
            this._animateTimerId = null;
        }
    }

    /**
     * Handles the animation loop for typing effect
     * @private
     */
    private _animate(): void {
        this._clearAnimateTimer();

        if (this.state.paused) {
            return;
        }

        // track the current active line
        this._getCursorPosition();

        // setTimeout is preferred over requestAnimationFrame so the interval
        // can be specified -- we can control how janky it looked; requestAnimationFrame
        // results in animation that's much to smooth for our purposes.
        this._animateTimerId = window.setTimeout(this._updateState, this._cursorInterval);
    }

    /**
     * Tracks cursor position and triggers onNewLine callback when needed
     * @private
     */
    private _getCursorPosition(): void {
        const { onNewLine } = this.props;
        // get the cursorRef
        const ref = this._cursorRef;
        let y = this._cursorY;

        if (ref && ref.current) {
            const node = ref.current;
            const top = node.offsetTop;
            if (y !== top) {
                // new line
                this._cursorY = top;
                onNewLine && onNewLine();
            }
        }
    }

    /**
     * Cleans up the animation timer
     * @private
     */
    private _clearAnimateTimer(): void {
        if (this._animateTimerId !== null) {
            window.clearTimeout(this._animateTimerId);
            this._animateTimerId = null;
        }
    }

    /**
     * Updates the component state for the next animation frame
     * @private
     */
    private _updateState(): void {
        const { text, } = this.props;
        const {
            char,
            active,
            done,
            paused,
        } = this.state;

        if (done) {
            return;
        }

        // let nextIndex = index;
        let nextChar = char;
        let nextActive = active;
        let nextDone = done;
        let nextPaused = paused;

        // if we're not active, we are now!
        if (!nextActive) {
            nextActive = true;
        }

        // if char is less that the current string, increment it
        if (char < text.length) {
            nextChar = char + 1;
        } else {
            nextActive = false;
            nextDone = true;
        }

        // update state
        this.setState({
            // index: nextIndex,
            char: nextChar,
            active: nextActive,
            done: nextDone,
            paused: nextPaused,
        });
    }

    /**
     * Triggers the onComplete callback when animation is finished
     * @private
     */
    private _onComplete(): void {
        const { onComplete, } = this.props;
        onComplete && onComplete();
    }
}

export default Teletype;
